'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface BrandingConfig {
  productName: string;
  tagline: string;
  logoUrl: string | null;
}

interface BrandingContextType {
  branding: BrandingConfig;
  updateBranding: (updates: Partial<BrandingConfig>) => Promise<void>;
  saving: boolean;
  saveError: string | null;
}

const CACHE_KEY = 'extio_branding_cache';

const defaultBranding: BrandingConfig = {
  productName: 'Extio Nexter',
  tagline: 'Project Management Simplified',
  logoUrl: null,
};

const BrandingContext = createContext<BrandingContextType>({
  branding: defaultBranding,
  updateBranding: async () => {},
  saving: false,
  saveError: null,
});

export const useBranding = () => useContext(BrandingContext);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig>(() => {
    if (typeof window === 'undefined') return defaultBranding;
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? { ...defaultBranding, ...JSON.parse(cached) } : defaultBranding;
    } catch {
      return defaultBranding;
    }
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/branding')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          const next: BrandingConfig = {
            productName: data.productName || defaultBranding.productName,
            tagline: data.tagline ?? defaultBranding.tagline,
            logoUrl: data.logoUrl ?? null,
          };
          setBranding(next);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  const updateBranding = useCallback(async (updates: Partial<BrandingConfig>) => {
    setSaving(true);
    setSaveError(null);
    try {
      const next = { ...branding, ...updates };
      const res = await fetch('/api/branding', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save branding');
      const saved: BrandingConfig = {
        productName: data.productName,
        tagline: data.tagline,
        logoUrl: data.logoUrl ?? null,
      };
      setBranding(saved);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(saved)); } catch {}
    } catch (e: any) {
      setSaveError(e?.message || 'Failed to save branding');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, updateBranding, saving, saveError }}>
      {children}
    </BrandingContext.Provider>
  );
}
